import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { Ticket, TicketStatus, TicketPriority } from '@/mocks/adminSupport';
import { statusMeta, priorityMeta, allStatuses, allPriorities } from '@/mocks/adminSupport';

interface TicketDetailModalProps {
  ticket: Ticket | null;
  onClose: () => void;
  onStatusChange: (ticket: Ticket, status: TicketStatus) => void;
  onPriorityChange: (ticket: Ticket, priority: TicketPriority) => void;
  onReply: (ticket: Ticket, message: string) => void;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return 'Hace ' + mins + 'm';
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return 'Hace ' + hrs + 'h';
  const days = Math.floor(hrs / 24);
  if (days < 7) return 'Hace ' + days + 'd';
  return new Date(dateStr).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

const actionsSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
  showConfirmButton: false,
  showCloseButton: true,
});

function showStatusPicker(current: TicketStatus, onChange: (st: TicketStatus) => void) {
  const items = allStatuses
    .map((st) => {
      const meta = statusMeta[st];
      const isCurrent = current === st;
      return '<button type="button" data-status="' + st + '" class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ' + (isCurrent ? 'bg-background-100' : 'hover:bg-background-100') + ' ' + meta.color + '"><span class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style="background:currentColor;opacity:0.1"><i class="' + meta.icon + ' text-base" style="color:currentColor"></i></span><span>' + meta.label + '</span>' + (isCurrent ? '<span class="ml-auto text-[10px] opacity-60">Actual</span>' : '') + '</button>';
    })
    .join('');

  actionsSwal.fire({
    title: 'Cambiar estado',
    html: '<div class="text-left space-y-1">' + items + '</div>',
    customClass: { popup: 'rounded-2xl max-w-xs', title: 'font-bold text-sm', htmlContainer: '!p-0' },
    didOpen: () => {
      const popup = Swal.getPopup();
      if (!popup) return;
      allStatuses.forEach((st) => {
        const el = popup.querySelector('[data-status="' + st + '"]');
        if (el) el.addEventListener('click', () => { Swal.close(); onChange(st); });
      });
    },
  });
}

function showPriorityPicker(current: TicketPriority, onChange: (p: TicketPriority) => void) {
  const items = allPriorities
    .map((p) => {
      const meta = priorityMeta[p];
      const isCurrent = current === p;
      return '<button type="button" data-priority="' + p + '" class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold text-left transition-colors cursor-pointer ' + (isCurrent ? 'bg-background-100' : 'hover:bg-background-100') + ' ' + meta.color + '"><span class="w-2 h-2 rounded-full shrink-0" style="background:currentColor"></span><span>' + meta.label + '</span>' + (isCurrent ? '<span class="ml-auto text-[10px] opacity-60">Actual</span>' : '') + '</button>';
    })
    .join('');

  actionsSwal.fire({
    title: 'Cambiar prioridad',
    html: '<div class="text-left space-y-1">' + items + '</div>',
    customClass: { popup: 'rounded-2xl max-w-xs', title: 'font-bold text-sm', htmlContainer: '!p-0' },
    didOpen: () => {
      const popup = Swal.getPopup();
      if (!popup) return;
      allPriorities.forEach((p) => {
        const el = popup.querySelector('[data-priority="' + p + '"]');
        if (el) el.addEventListener('click', () => { Swal.close(); onChange(p); });
      });
    },
  });
}

export default function TicketDetailModal({ ticket, onClose, onStatusChange, onPriorityChange, onReply }: TicketDetailModalProps) {
  const [replyText, setReplyText] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ticket) {
      setReplyText('');
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [ticket]);

  if (!ticket) return null;

  const sMeta = statusMeta[ticket.status];
  const pMeta = priorityMeta[ticket.priority];
  const canReply = ticket.status === 'abierto' || ticket.status === 'proceso';

  const handleSendReply = () => {
    if (!replyText.trim()) {
      SwalAlert.warning('Respuesta vacía', 'Escribí un mensaje para responder.');
      return;
    }
    onReply(ticket, replyText.trim());
    setReplyText('');
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleStatusClick = () => {
    showStatusPicker(ticket.status, (st) => {
      if (ticket.status === st) return;
      if (st === 'cerrado') {
        SwalAlert.confirm('¿Cerrar ticket?', 'Vas a cerrar el ticket #' + ticket.id + '. El solicitante será notificado.').then((result) => {
          if (result.isConfirmed) onStatusChange(ticket, st);
        });
        return;
      }
      onStatusChange(ticket, st);
    });
  };

  const handlePriorityClick = () => {
    showPriorityPicker(ticket.priority, (p) => onPriorityChange(ticket, p));
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center shrink-0">
              <i className="ri-ticket-line text-primary-600 text-base"></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm truncate">Ticket #{ticket.id}</h3>
              <p className="text-foreground-400 text-[10px] truncate">{ticket.requester} · {timeAgo(ticket.createdAt)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
          >
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Subject + meta */}
        <div className="px-5 py-3 border-b border-background-100 shrink-0">
          <h4 className="text-foreground-950 font-bold text-sm mb-2.5">{ticket.subject}</h4>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleStatusClick}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-opacity hover:opacity-80 ${sMeta.bgColor} ${sMeta.color}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${sMeta.dot}`}></span>
              {sMeta.label}
              <i className="ri-arrow-down-s-line text-[10px]"></i>
            </button>
            <button
              onClick={handlePriorityClick}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-bold cursor-pointer transition-opacity hover:opacity-80 ${pMeta.bgColor} ${pMeta.color}`}
            >
              {pMeta.label}
              <i className="ri-arrow-down-s-line text-[10px]"></i>
            </button>
            <span className="text-foreground-400 text-[10px] ml-auto flex items-center gap-1">
              <i className="ri-shield-user-line text-[9px]"></i>
              {ticket.assignedTo}
            </span>
          </div>
        </div>

        {/* Chat body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Original message */}
          <div className="p-3.5 rounded-2xl bg-background-100 border border-background-200/70">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-user-line text-foreground-500 text-[10px]"></i>
              </div>
              <span className="text-foreground-800 text-[11px] font-bold">{ticket.requester}</span>
              <span className="text-foreground-400 text-[9px]">{timeAgo(ticket.createdAt)}</span>
            </div>
            <p className="text-foreground-700 text-xs leading-relaxed whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Replies */}
          {ticket.replies.map((reply) => {
            const isSupport = reply.authorRole === 'soporte' || reply.authorRole === 'admin';
            return (
              <div
                key={reply.id}
                className={`p-3.5 rounded-2xl border ${
                  isSupport
                    ? 'bg-primary-50 border-primary-100 ml-6'
                    : 'bg-background-100 border-background-200/70 mr-6'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${isSupport ? 'bg-primary-200' : 'bg-background-200'}`}>
                    <i className={`text-[10px] ${isSupport ? 'ri-shield-user-line text-primary-600' : 'ri-user-line text-foreground-500'}`}></i>
                  </div>
                  <span className="text-foreground-800 text-[11px] font-bold">{reply.author}</span>
                  {isSupport && (
                    <span className="px-1.5 py-0.5 rounded-md text-[8px] font-bold bg-primary-200 text-primary-700">Soporte</span>
                  )}
                  <span className="text-foreground-400 text-[9px]">{timeAgo(reply.createdAt)}</span>
                </div>
                <p className="text-foreground-700 text-xs leading-relaxed whitespace-pre-wrap">{reply.message}</p>
              </div>
            );
          })}

          {ticket.replies.length === 0 && (
            <div className="text-center py-8">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-background-100 flex items-center justify-center mb-3">
                <i className="ri-chat-3-line text-foreground-400 text-lg"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-500">Sin respuestas aún</p>
              <p className="text-xs text-foreground-400 mt-1">Sé el primero en responder a este ticket.</p>
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Reply input */}
        {canReply && (
          <div className="px-5 py-4 border-t border-background-200 shrink-0 bg-background-50">
            <div className="flex items-start gap-3">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Escribí tu respuesta..."
                rows={2}
                className="flex-1 px-3.5 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all resize-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSendReply();
                  }
                }}
              ></textarea>
              <button
                onClick={handleSendReply}
                className="shrink-0 w-10 h-10 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors cursor-pointer"
                title="Enviar (Cmd+Enter)"
              >
                <i className="ri-send-plane-fill text-base"></i>
              </button>
            </div>
            <p className="text-[9px] text-foreground-400 mt-1.5 ml-1">Cmd + Enter para enviar</p>
          </div>
        )}

        {/* Closed banner */}
        {!canReply && (
          <div className="px-5 py-4 border-t border-background-200 shrink-0 bg-background-100">
            <div className="flex items-center gap-2.5 text-foreground-500">
              <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center shrink-0">
                <i className="ri-lock-line text-foreground-400 text-sm"></i>
              </div>
              <div>
                <p className="text-[11px] font-semibold">Ticket {ticket.status === 'cerrado' ? 'cerrado' : 'resuelto'}</p>
                <p className="text-[10px] text-foreground-400">Las respuestas están deshabilitadas. Cambiá el estado para reabrir.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}